import i18n from '@/utils/i18n';
import React, { useEffect, useRef, useState } from 'react';
import {
  ACCOUNT_SCREEN_WIDTH,
  RestaurantAccountComponent,
} from './restaurant-account-component';
import {
  APP_MODAL_ROUTES,
  EMPLOYEE_MANAGEMENT_ROUTES,
} from '@/navigators/routes';
import { AppModalStackParams, RestaurantStackParams } from '@/navigators/types';
import { Button } from '@/components/button';
import { CompositeNavigationProp } from '@react-navigation/native';
import { DeleteAccountActionSheet } from './delete-account-action-sheet';
import { Disclosures } from '@/components/disclosures';
import { EmployeeRole, RestaurantEmployeeFragment } from '@/types';
import {
  LIST_MARGIN_HORIZONTAL,
  SCREEN_PADDING_LARGE,
  SPLIT_CONTAINER_MARGIN_HORIZONTAL,
} from '@/styles';
import { RemoveEmployeeActionSheet } from '@/components/restaurant/remove-employee-action-sheet';
import { RestaurantEmployeeList } from '@/components/restaurant/restaurant-employee-list';
import { SplitPageContainer } from '@/components/split-page-container';
import { StackNavigationProp } from '@react-navigation/stack';
import { StyleSheet, View } from 'react-native';
import { UpdateEmployeeFooter } from '@/components/restaurant/update-employee-footer';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useRestaurantEmployees } from '@/hooks/use-restaurant-employees';

type Navigation = CompositeNavigationProp<
  StackNavigationProp<
    RestaurantStackParams,
    EMPLOYEE_MANAGEMENT_ROUTES.Employees
  >,
  StackNavigationProp<AppModalStackParams, APP_MODAL_ROUTES.LocationManager>
>;

interface Props {
  navigation: Navigation;
}

export const SplitRestaurantEmployeesScreen: React.FC<Props> = ({
  navigation,
}) => {
  const { selectedRestaurantRelationship, supplierEmployeeRole } =
    useAuthenticatedUserContext();

  const [selectedEmployee, setSelectedEmployee] =
    useState<RestaurantEmployeeFragment | null>(null);
  const [employeeToRemove, setEmployeeToRemove] =
    useState<RestaurantEmployeeFragment | null>(null);
  const [showDeleteAccountActionSheet, setShowDeleteAccountActionSheet] =
    useState(false);

  const canUpdateEmployeeInfo = supplierEmployeeRole === EmployeeRole.Admin;
  const canUpdateEmployees =
    supplierEmployeeRole ||
    selectedRestaurantRelationship.employeeRole === EmployeeRole.Admin;

  const { restaurantEmployees, loading } = useRestaurantEmployees();

  const firstEmployeeSet = useRef(false);

  useEffect(() => {
    if (!firstEmployeeSet.current && restaurantEmployees.length) {
      setSelectedEmployee(restaurantEmployees[0]);
      firstEmployeeSet.current = true;
    }
  }, [restaurantEmployees]);

  const updateSelectedEmployee = (
    updatedEmployee: Partial<RestaurantEmployeeFragment>,
  ) => {
    setSelectedEmployee((previousSelectedEmployee) => ({
      ...previousSelectedEmployee!,
      ...updatedEmployee,
    }));
  };

  return (
    <>
      <SplitPageContainer
        primaryComponent={
          <View style={styles.employeeListContainer}>
            <RestaurantEmployeeList
              loading={loading}
              onEmployeeSelect={(businessEmployee) => {
                setSelectedEmployee(businessEmployee);
              }}
              restaurantEmployees={restaurantEmployees}
              selectedRestaurantEmployeeUUID={
                selectedEmployee?.restaurant_employee_id
              }
            />
          </View>
        }
        title={i18n.t('employees')}
      >
        {selectedEmployee ? (
          <View style={styles.contentContainer}>
            <View style={styles.content}>
              <View collapsable={false}>
                <RestaurantAccountComponent
                  contactEmail={selectedEmployee.employee_contact_email}
                  contactPhoneNumber={
                    selectedEmployee.employee_contact_phone_number
                  }
                  employeeName={selectedEmployee.employee_name}
                  employeeUUID={selectedEmployee.employee_id}
                  ListFooterComponent={
                    canUpdateEmployees ? (
                      <UpdateEmployeeFooter
                        businessEmployee={selectedEmployee}
                        onUpdate={updateSelectedEmployee}
                      />
                    ) : undefined
                  }
                  loginEmail={selectedEmployee.employee_login_email}
                  onPressNotifications={
                    canUpdateEmployees
                      ? () => {
                          navigation.navigate(
                            APP_MODAL_ROUTES.AccountNotificationSettings,
                            {
                              userUUID: selectedEmployee.employee_id,
                            },
                          );
                        }
                      : undefined
                  }
                />
                {canUpdateEmployeeInfo ? (
                  <Button
                    onPress={() => {
                      navigation.navigate(APP_MODAL_ROUTES.UpdateEmployee, {
                        contactProtected:
                          selectedEmployee.employee_contact_protected,
                        employeeUUID: selectedEmployee.employee_id,
                        initialEmail:
                          selectedEmployee.employee_contact_email ?? null,
                        initialLoginEmail:
                          selectedEmployee.employee_login_email ?? null,
                        initialName: selectedEmployee.employee_name,
                        initialPhoneNumber:
                          selectedEmployee.employee_contact_phone_number,
                        onUpdate: updateSelectedEmployee,
                      });
                    }}
                    style={styles.employeeActionButton}
                    trackingData={{ name: 'account.update_employee_button' }}
                  >
                    {i18n.t('update_employee')}
                  </Button>
                ) : null}
                {canUpdateEmployees ? (
                  <Button
                    onPress={() => {
                      setEmployeeToRemove(selectedEmployee);
                    }}
                    style={styles.employeeActionButton}
                    trackingData={{ name: 'account.remove_employee_button' }}
                    type="danger"
                  >
                    {i18n.t('remove_employee')}
                  </Button>
                ) : null}
              </View>
              <Disclosures />
            </View>
          </View>
        ) : null}
      </SplitPageContainer>
      <RemoveEmployeeActionSheet
        businessName={selectedRestaurantRelationship.name}
        businessUUID={selectedRestaurantRelationship.uuid}
        employeeName={employeeToRemove?.employee_name || ''}
        employeeUUID={employeeToRemove?.employee_id || ''}
        isVisible={Boolean(employeeToRemove)}
        onClose={(employeeRemoved: boolean) => {
          setEmployeeToRemove(null);
          if (employeeRemoved) {
            setSelectedEmployee(null);
          }
        }}
        supplierUUID={selectedRestaurantRelationship.supplierUUID}
      />
      {showDeleteAccountActionSheet ? (
        <DeleteAccountActionSheet
          onClose={() => setShowDeleteAccountActionSheet(false)}
        />
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
    justifyContent: 'space-between',
    maxWidth: ACCOUNT_SCREEN_WIDTH,
    paddingBottom: SCREEN_PADDING_LARGE,
    paddingHorizontal: SCREEN_PADDING_LARGE,
  },
  contentContainer: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    overflow: 'scroll',
  },
  employeeActionButton: {
    marginTop: 8,
  },
  employeeListContainer: {
    flex: 1,
    marginHorizontal:
      SPLIT_CONTAINER_MARGIN_HORIZONTAL - LIST_MARGIN_HORIZONTAL,
  },
});
